// Types mirroring backend Pydantic schemas. Keep in sync with
// backend/app/schemas/jobs.py and backend/app/schemas/enums.py.

export const RIGHTS_TYPES = [
  "owned_content",
  "permission_granted",
  "public_domain",
  "licensed_for_reuse",
] as const;

export type RightsType = (typeof RIGHTS_TYPES)[number];

export const JOB_STATUSES = [
  "pending",
  "validating",
  "extracting_audio",
  "separating_audio",
  "compressing_stems",
  "transcribing",
  "generating_subtitles",
  "completed",
  "failed",
  "cancelled",
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set([
  "completed",
  "failed",
  "cancelled",
]);

export const PIPELINE_STAGES = [
  { key: "validating", labelKey: "progress.stages.validating" },
  { key: "extracting_audio", labelKey: "progress.stages.extractingAudio" },
  { key: "separating_audio", labelKey: "progress.stages.separatingAudio" },
  { key: "compressing_stems", labelKey: "progress.stages.compressingStems" },
  { key: "transcribing", labelKey: "progress.stages.transcribing" },
  { key: "generating_subtitles", labelKey: "progress.stages.generatingSubtitles" },
] as const;
export type PipelineStageKey = (typeof PIPELINE_STAGES)[number]["key"];

export interface CreateJobRequest {
  youtube_url: string;
  rights_confirmation: true;
  rights_type: RightsType;
  target_language?: string;
}

export interface CreateJobResponse {
  job_id: string;
  status: JobStatus;
}

export interface JobStatusResponse {
  job_id: string;
  status: JobStatus;
  progress: number;
  current_step: string | null;
  error: string | null;
}

export interface SongSummary {
  job_id: string;
  title: string;
}

export interface PlaylistSummary {
  id: string;
  slug: string;
  name: string;
  songs: SongSummary[];
}

export interface LibraryResponse {
  recent_songs: SongSummary[];
  playlists: PlaylistSummary[];
}

export interface SongDetail {
  job_id: string;
  title: string;
  duration_seconds: number | null;
  vocals_audio_url: string | null;
  instrumental_audio_url: string | null;
  subtitles_url: string | null;
}

export interface ApiError {
  detail: string | Array<{ msg: string; loc?: string[] }>;
}

export interface AdminSession {
  id: string;
  expires_at: string;
}

export interface AdminLoginResponse {
  session: AdminSession;
}

export interface AdminJobStep {
  name: string;
  status: string;
  attempt: number;
  provider_used: string | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface AdminJobSummary {
  id: string;
  status: JobStatus;
  progress: number;
  current_step: string | null;
  error: string | null;
  youtube_url: string;
  youtube_video_id: string | null;
  source_title: string | null;
  source_channel: string | null;
  source_duration_seconds: number | null;
  target_language: string;
  created_at: string;
  updated_at: string;
}

export interface AdminJobDetail extends AdminJobSummary {
  steps: AdminJobStep[];
}

export interface AdminJobListResponse {
  jobs: AdminJobSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminEditableSettings {
  max_source_duration_seconds: number;
  lyric_line_max_chars: number;
  lyric_line_max_words: number;
  lyric_line_min_words: number;
  karaoke_word_highlighting: boolean;
  provider_metadata: string;
  provider_audio_extraction: string;
  provider_separation: string;
  provider_transcription: string;
  provider_subtitles: string;
  deepgram_model: string;
  lalalai_splitter: string;
  lalalai_dereverb: boolean;
}

export interface AdminReadOnlySettings {
  database_configured: boolean;
  redis_configured: boolean;
  celery_configured: boolean;
  storage_provider: string;
  storage_root_configured: boolean;
  deepgram_api_key_configured: boolean;
  lalal_ai_api_key_configured: boolean;
  s3_configured: boolean;
}

export interface AdminProviderOptions {
  metadata: string[];
  audio_extraction: string[];
  separation: string[];
  transcription: string[];
  subtitles: string[];
  lalalai_splitters: string[];
}

export interface AdminSettingsResponse {
  editable: AdminEditableSettings;
  read_only: AdminReadOnlySettings;
  provider_options: AdminProviderOptions;
}

export type AdminSettingsPatch = Partial<AdminEditableSettings>;

export interface AdminUserSummary {
  id: string;
  email: string | null;
  created_at: string;
  job_count: number;
  latest_job_at: string | null;
}

export interface AdminUsersResponse {
  users: AdminUserSummary[];
}

export interface AdminDashboardResponse {
  job_counts: Record<string, number>;
  recent_jobs: AdminJobSummary[];
  library: LibraryResponse;
  settings: AdminSettingsResponse;
}
